import { useSortable } from "@dnd-kit/react/sortable";
import { faGripDotsVertical } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Flex } from "@mantine/core";
import type { Cell, Row } from "@tanstack/react-table";
import { type ReactNode, useCallback } from "react";
import { type ClassNameValue, twMerge } from "tailwind-merge";

import { isFunction, type SingleOrArray } from "@/types/utils";

type ClassNameOrClassNameGetter<T> =
  | SingleOrArray<ClassNameValue>
  | ((tableItem: T, isDragging: boolean) => SingleOrArray<ClassNameValue>);

interface SortableTableRowProps<TData> {
  cellTDClassName?: ClassNameOrClassNameGetter<Cell<TData, unknown>>;
  className?: ClassNameOrClassNameGetter<Row<TData>>;
  disabled?: boolean;
  id: string;
  index: number;
  isVoice?: boolean;
  renderCell: (cell: Cell<TData, unknown>) => ReactNode;
  row: Row<TData>;
}

export const SortableTableRow = <TData,>({
  cellTDClassName,
  className,
  disabled = false,
  id,
  index,
  isVoice = false,
  renderCell,
  row,
}: SortableTableRowProps<TData>) => {
  const { handleRef, isDragging, ref } = useSortable({
    // Due to the the limit of a single voice question, they are never draggable
    disabled: disabled || isVoice,
    id,
    index,
  });
  const getRowClassName = useCallback(
    (row: Row<TData>) =>
      isFunction(className) ? className(row, isDragging) : className,
    [className, isDragging],
  );
  const getCellClassName = useCallback(
    (cell: Cell<TData, unknown>) =>
      isFunction(cellTDClassName)
        ? cellTDClassName(cell, isDragging)
        : cellTDClassName,
    [cellTDClassName, isDragging],
  );

  return (
    <tr className={twMerge(getRowClassName(row))} ref={ref}>
      {row.getVisibleCells().map((cell, cellIndex) => (
        <td className={twMerge(getCellClassName(cell))} key={cell.id}>
          {cellIndex === 0 && !disabled ? (
            <Flex gap="md">
              <div
                className={isVoice ? "cursor-default" : "cursor-grab"}
                ref={handleRef}
              >
                <FontAwesomeIcon
                  icon={faGripDotsVertical}
                  className="text-gray-300"
                  size="xl"
                />
              </div>
              {renderCell(cell)}
            </Flex>
          ) : (
            renderCell(cell)
          )}
        </td>
      ))}
    </tr>
  );
};
